/**
 * Helpers for a single filing page (/drhp-filings/:id), backed by
 * GET /api/core/ipos/drhp-tracker/{id}.
 *
 * The response is the tracker row (board, regulatory status, filing
 * timestamps, pipeline status) plus the same json_data the catalog serves, so
 * mapIpoDetailResponse does the generic mapping and everything here is about
 * deciding what is safe to show.
 *
 * The json_data comes out of an automated PDF extractor, and a lot of it is
 * wrong in ways that are easy to detect: cover-page labels glued to names,
 * prose where a number belongs ("EBITDA Margin is an indicator of..."), a
 * sentence fragment in a date field, offer text in the address field. Every
 * getter below validates before it returns, so a section is either right or
 * absent -- it is never a guess.
 */
import { resolveApiUrl } from '../api/axiosClient';
import { cleanAddress, registrarPortal } from './issueSubscription';
import { istDate, leadManagersFromDetail, stripExtractionLabels } from './drhpTracker';
import { buildCompanyNews } from './companyNews';

// Kept as named exports here: this page's tests and callers already use them
export { buildCompanyNews as buildFilingNews, companyNamePhrases } from './companyNews';

export const PIPELINE_STATES = {
  approved: {
    label: 'Analysis published',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    note: 'The DRHP has been parsed and its extracted data reviewed.',
  },
  pending_admin_review: {
    label: 'Extraction in review',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    note: 'The filing is logged, but its extracted data has not been released yet.',
  },
  drhp_downloaded: {
    label: 'Awaiting extraction',
    badge: 'bg-slate-100 text-slate-600 border-slate-200',
    note: 'The filing has been collected but not parsed yet.',
  },
  failed_extraction: {
    label: 'Extraction failed',
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
    note: 'The document could not be parsed, so only the filing record is available.',
  },
};

export const pipelineState = (status) =>
  PIPELINE_STATES[status] || { label: 'Not processed', badge: 'bg-slate-100 text-slate-600 border-slate-200', note: '' };

// --- field validators -------------------------------------------------------

const text = (value) => (typeof value === 'string' && value.trim() ? value.trim().replace(/\s+/g, ' ') : null);

/**
 * A prose note lifted out of the offer document. The extractor cuts these at a
 * fixed length and often starts them mid-sentence, so the cut is shown at both
 * ends rather than passed off as a complete quotation.
 */
const LIMIT = 260;
export const prose = (value, limit = LIMIT) => {
  const flat = text(value);
  if (!flat || flat.length < 12) return null;
  const opened = /^[a-z]/.test(flat) ? `…${flat}` : flat;
  if (opened.length <= limit) return opened;
  const cut = opened.slice(0, limit);
  return `${cut.slice(0, cut.lastIndexOf(' ') > limit - 40 ? cut.lastIndexOf(' ') : limit).replace(/[\s,;:-]+$/, '')}…`;
};

// A figure, not prose: "1,885.93", "15.95*", "12.76%", 115
const NUMERIC_TEXT = /^[₹$\s]*-?[\d,]+(?:\.\d+)?\s*%?\*?$/;
export const numericText = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  const flat = text(value);
  return flat && NUMERIC_TEXT.test(flat) ? flat : null;
};

const isPercent = (value) => typeof value === 'string' && value.includes('%');

// "Friday, September 25, 2026", "16-SEP-2026", "September 21, 2026", ISO -- but
// not "shall be one Working Day prior to the", which the extractor also stores
// in bid-date fields
export const filingDateText = (value) => {
  const flat = text(value);
  if (!flat || flat.length > 40) return null;
  const hasDay = /\b\d{1,2}\b/.test(flat);
  const hasYear = /\b(?:19|20)\d{2}\b/.test(flat);
  const hasMonth = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(flat);
  return hasDay && hasYear && hasMonth ? flat : null;
};

const URL_LIKE = /^(?:https?:\/\/|www\.)[\w-]+(?:\.[\w-]+)+/i;
export const websiteUrl = (value) => {
  const flat = text(value)?.replace(/[,;]+$/, '');
  if (!flat || !URL_LIKE.test(flat)) return null;
  return /^https?:\/\//i.test(flat) ? flat : `https://${flat}`;
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const email = (value) => {
  // The extractor keeps the cover page's separators ("secretarial@x.in;")
  const flat = text(value)?.replace(/[;,.]+$/, '');
  return flat && EMAIL.test(flat) ? flat : null;
};

// "+91 40 6716 2222" -- at least 8 digits, nothing but phone punctuation
const phone = (value) => {
  const flat = text(value);
  return flat && /^[+\d][\d\s()+-]{7,}$/.test(flat) && (flat.match(/\d/g) || []).length >= 8 ? flat : null;
};

const CIN = /^[LU]\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/;

// "www.bigshareonline.com/" and "ipo@bigshareonline.com" both -> "bigshareonline.com"
const domainOf = (value) => {
  const flat = text(value)?.toLowerCase();
  if (!flat) return null;
  const host = flat.includes('@') ? flat.split('@').pop() : flat.replace(/^https?:\/\//, '').split('/')[0];
  return host?.replace(/^www\./, '').replace(/[^a-z0-9.-]/g, '') || null;
};

// "Mrs. Payal Jain SEBI Registration No" -> "Mrs. Payal Jain"; junk like "Sh" drops out
const personName = (value) => {
  const flat = stripExtractionLabels(value)
    .replace(/\b(?:sebi\s+registration|registration)\s*(?:no\.?|number)?\.?\s*:?.*$/i, '')
    .replace(/[,;:]+$/, '')
    .trim();
  return flat.length >= 4 && /\s/.test(flat) ? flat : null;
};

// FY keys the extractor uses ("fy_2025") -> "FY25", newest last
const fyLabel = (key) => `FY${String(key).replace(/\D/g, '').slice(-2)}`;
const byFinancialYear = (a, b) => String(a).localeCompare(String(b));

// --- sections ---------------------------------------------------------------

const KPI_LABELS = {
  ebitda_margin: 'EBITDA margin',
  pat_margin_percent: 'PAT margin',
  roe_percent: 'Return on equity',
  roce_percent: 'Return on capital employed',
  ronw_percent: 'Return on net worth',
};

const FINANCIAL_ROWS = [
  { keys: ['revenue_lakhs', 'revenue'], label: 'Revenue from operations' },
  { keys: ['pat_lakhs', 'pat'], label: 'Profit after tax' },
  { keys: ['ebitda_lakhs', 'ebitda'], label: 'EBITDA' },
  { keys: ['net_worth_lakhs', 'net_worth'], label: 'Net worth' },
];

/**
 * The restated financials, as a table of years x metrics.
 *
 * The unit lives on the block ("₹ crore"), never in the key -- "revenue_lakhs"
 * holds crore figures on plenty of records -- so the stated unit wins and the
 * key is only used to find the row. Percentages and prose in an amount row are
 * extraction errors and are dropped.
 */
export const buildFinancialTable = (financial = {}) => {
  const summary = financial.restated_summary || {};
  const unit = text(summary.unit) || (text(financial.unit) ? `₹ ${text(financial.unit)}` : null);

  const rows = FINANCIAL_ROWS.map(({ keys, label }) => {
    const source = keys.map((key) => summary[key]).find((value) => value && typeof value === 'object');
    if (!source) return null;
    const values = Object.entries(source)
      .filter(([, value]) => numericText(value) && !isPercent(value))
      .sort(([a], [b]) => byFinancialYear(a, b))
      .map(([year, value]) => ({ year: fyLabel(year), value: numericText(value) }));
    return values.length ? { label, values } : null;
  }).filter(Boolean);

  if (!rows.length) return null;
  const years = [...new Set(rows.flatMap((row) => row.values.map((entry) => entry.year)))].sort();
  return {
    unit,
    years,
    rows: rows.map((row) => ({
      label: row.label,
      cells: years.map((year) => row.values.find((entry) => entry.year === year)?.value || null),
    })),
  };
};

// RoNW is the offer document's own term; the backend stores ROE as a copy of it
const KPI_ORDER = ['ronw_percent', 'roe_percent', 'roce_percent', 'pat_margin_percent', 'ebitda_margin'];

/**
 * Ratio KPIs by year.
 *
 * The extractor sometimes stores the metric's *definition* where its value
 * belongs, and a margin computed off a misread revenue line comes out above
 * 100% -- a share of revenue cannot exceed it -- so both are dropped. Returns
 * on equity are left alone: those legitimately pass 100% for an asset-light
 * issuer.
 */
export const buildKpiTable = (derived = {}) => {
  const rows = Object.entries(derived.kpis || {})
    .filter(([, byYear]) => byYear && typeof byYear === 'object')
    .sort(([a], [b]) => (KPI_ORDER.indexOf(a) + 1 || 99) - (KPI_ORDER.indexOf(b) + 1 || 99))
    .map(([key, byYear]) => {
      const capped = /margin/i.test(key);
      const values = Object.entries(byYear)
        .filter(([, value]) => numericText(value) && !(capped && Math.abs(parseFloat(String(value).replace(/,/g, ''))) > 100))
        .sort(([a], [b]) => byFinancialYear(a, b))
        .map(([year, value]) => ({ year: fyLabel(year), value: numericText(value) }));
      return values.length ? { label: KPI_LABELS[key] || key.replace(/_/g, ' '), values } : null;
    })
    .filter(Boolean)
    // ROE and RoNW arrive as the same numbers under two names; show them once
    .filter((row, index, all) => {
      const signature = JSON.stringify(row.values);
      return all.findIndex((other) => JSON.stringify(other.values) === signature) === index;
    });

  if (!rows.length) return null;
  const years = [...new Set(rows.flatMap((row) => row.values.map((entry) => entry.year)))].sort();
  return {
    years,
    rows: rows.map((row) => ({
      label: row.label,
      cells: years.map((year) => row.values.find((entry) => entry.year === year)?.value || null),
    })),
  };
};

/**
 * The issue's own terms. fresh_issue_amount is stored without its unit and
 * DRHP cover pages quote lakhs, millions or crore, so the figure is shown as
 * filed and flagged rather than converted to a rupee amount we can't verify.
 */
export const buildIssueTerms = (structure = {}, valuation = {}) => {
  const reservation = structure.reservation_structure || {};
  const band = valuation.price_band || {};
  const floor = numericText(band.floor_price);
  const cap = numericText(band.cap_price);

  const terms = [
    { label: 'Issue type', value: text(structure.ipo_type) },
    { label: 'Face value', value: numericText(structure.face_value_per_share) ? `₹${numericText(structure.face_value_per_share)} per share` : null },
    { label: 'Price band', value: floor && cap ? (floor === cap ? `₹${cap}` : `₹${floor} – ₹${cap}`) : null },
    { label: 'Fresh issue', value: numericText(structure.fresh_issue_amount), unitUnknown: true },
    { label: 'Fresh issue (shares)', value: numericText(structure.fresh_issue_shares) },
    { label: 'Total issue size (shares)', value: numericText(structure.total_issue_size_shares) },
    { label: 'Offer for sale', value: text(structure.offer_for_sale) },
    { label: 'Lot size', value: numericText(structure.lot_size) ? `${numericText(structure.lot_size)} shares` : null },
    { label: 'Listing exchanges', value: (Array.isArray(structure.listing_exchanges) ? structure.listing_exchanges.filter(Boolean) : []).join(' · ') || null },
  ].filter((row) => row.value);

  const quota = [
    { label: 'QIB', value: numericText(reservation.qib_max_percent), qualifier: 'not more than' },
    { label: 'NII', value: numericText(reservation.non_institutional_min_percent), qualifier: 'not less than' },
    { label: 'Retail', value: numericText(reservation.retail_min_percent), qualifier: 'not less than' },
  ].filter((row) => row.value);

  return { terms, quota, quotaNote: prose(reservation.details) };
};

/** The bidding calendar, when the DRHP states one (most drafts don't). */
export const buildBiddingDates = (structure = {}) =>
  [
    { label: 'Anchor investor bid date', value: filingDateText(structure.anchor_investor_bid_date) },
    { label: 'Bidding opens', value: filingDateText(structure.bid_issue_opens) },
    { label: 'Bidding closes', value: filingDateText(structure.bid_issue_closes) },
  ].filter((row) => row.value);


/** Generated Q&A. Answers that never got their blanks filled are dropped. */
export const buildFilingFaqs = (json = {}) =>
  (Array.isArray(json.faqs) ? json.faqs : [])
    .map((item) => ({ id: text(item?.id), question: text(item?.question), answer: text(item?.answer) }))
    .filter((item) => item.question && item.answer && !/yet to be announced|not available|\[●\]/i.test(item.answer));

const LITIGATION_COLUMNS = [
  { key: 'criminal', label: 'Criminal' },
  { key: 'tax', label: 'Tax' },
  { key: 'statutory', label: 'Statutory / regulatory' },
  { key: 'civil', label: 'Civil' },
  { key: 'amount_lakhs', label: 'Amount involved' },
];

export const buildLitigation = (risk = {}) => {
  const rows = (Array.isArray(risk.outstanding_litigations_table) ? risk.outstanding_litigations_table : [])
    .map((row) => ({ category: text(row?.category), cells: LITIGATION_COLUMNS.map((column) => text(row?.[column.key])) }))
    .filter((row) => row.category && row.cells.some(Boolean));
  if (!rows.length) return null;
  // Keep only the columns at least one row fills
  const keep = LITIGATION_COLUMNS.map((_, index) => rows.some((row) => row.cells[index]));
  return {
    columns: LITIGATION_COLUMNS.filter((_, index) => keep[index]),
    rows: rows.map((row) => ({ category: row.category, cells: row.cells.filter((_, index) => keep[index]) })),
  };
};

/**
 * The issuer's own details: registered office, promoters, bankers, registrar.
 */
export const buildFilingParties = (detail, basic = {}) => {
  // The extractor reads the cover page's contact blocks into each other: the
  // issuer's website turns up under the registrar, the registrar's mailbox
  // under the issuer. Whoever owns the domain keeps the contact.
  const issuerDomain = domainOf(detail?.companyWebsite);
  const registrarDomain = domainOf(detail?.registrar?.email);
  const belongsToIssuer = (value) => {
    const domain = domainOf(value);
    return !domain || !registrarDomain || domain !== registrarDomain;
  };
  const belongsToRegistrar = (value) => {
    const domain = domainOf(value);
    return !domain || !issuerDomain || domain !== issuerDomain;
  };

  const registrarName = stripExtractionLabels(detail?.registrar?.name);
  const registrar =
    registrarName.length > 3
      ? {
          name: registrarName,
          email: email(detail.registrar?.email),
          phone: phone(detail.registrar?.telephone),
          website: belongsToRegistrar(detail.registrar?.website) ? websiteUrl(detail.registrar?.website) : null,
          sebi: text(detail.registrar?.sebi_registration),
          portal: registrarPortal(registrarName),
        }
      : null;

  const leadManagers = leadManagersFromDetail(detail);
  const leadManager = leadManagers.length
    ? {
        names: leadManagers,
        email: email(detail.leadManagerObj?.email),
        phone: phone(detail.leadManagerObj?.phone),
        website: websiteUrl(detail.leadManagerObj?.website),
        sebi: text(detail.leadManagerObj?.sebiRegistration),
      }
    : null;

  return {
    registrar,
    leadManager,
    promoters: (Array.isArray(basic.promoter_names) ? basic.promoter_names : []).map(personName).filter(Boolean),
    registeredOffice: cleanAddress(detail?.registeredOffice),
    complianceOfficer: personName(detail?.officeContactPerson),
    website: websiteUrl(detail?.companyWebsite),
    email: belongsToIssuer(detail?.contactEmail) ? email(detail?.contactEmail) : null,
    phone: phone(detail?.contactPhone),
    incorporatedOn: filingDateText(basic.incorporation_date),
  };
};

/**
 * The LLM summary the backend stores (portal_narrative). It is written by a
 * small local model from the extracted text and gets basic facts wrong --
 * currencies, countries, which year a figure belongs to -- so it is returned
 * separately from the filed data and the page labels it as generated.
 */
export const buildNarrative = (portal = {}) => {
  const summary = text(portal.summary_paragraph);
  const bullets = (Array.isArray(portal.snapshot_bullets) ? portal.snapshot_bullets : []).map(text).filter(Boolean);
  const highlights = (Array.isArray(portal.press_highlights) ? portal.press_highlights : []).map(text).filter(Boolean);
  const quote = text(portal.founder_quote);
  if (!summary && !bullets.length && !highlights.length && !quote) return null;
  return { summary, bullets, highlights, quote, model: text(portal.llm_model) };
};

/**
 * Everything the page renders, from one tracker-detail response.
 *
 * @param {object} record - response.data of GET /api/core/ipos/drhp-tracker/{id}
 * @param {object} detail - mapIpoDetailResponse(record)
 */
export const buildFilingView = (record, detail) => {
  if (!record) return null;
  const json = record.json_data || {};
  const basic = json.basic_company_details || {};
  const board = ['mainboard', 'sme'].includes(record.board) ? record.board : null;
  const cin = text(record.cin) || text(basic.cin);
  const company = text(record.company_name) || text(basic.company_name) || 'Unnamed filing';

  return {
    id: String(record.id),
    company,
    cin: cin && CIN.test(cin) ? cin : null,
    board,
    logoUrl: basic.logo_url ? resolveApiUrl(basic.logo_url) : null,
    // 'approved' on the tracker means the RHP is in, not that SEBI cleared the issue
    regulatoryStatus: record.status === 'approved' ? 'rhp_filed' : 'under_review',
    pipeline: pipelineState(record.pipeline_status),
    pipelineStatus: record.pipeline_status || null,
    hasAnalysis: record.pipeline_status === 'approved',

    // The document's own date vs. when this tracker picked it up -- they differ
    // by days and only the first is a filing date
    drhpDated: filingDateText(json.drhp_date),
    rhpDated: filingDateText(json.rhp_date),
    drhpAddedOn: istDate(record.drhp_filed_on),
    rhpAddedOn: istDate(record.rhp_filed_on),

    sourceDrhp: text(json.source_file),
    sourceRhp: text(json.source_rhp),
    extractedOn: text(json.extraction_timestamp),
    extractorVersion: text(json.extraction_meta?.extractor_version),
    pagesRead: Number.isFinite(json.extraction_meta?.pages_loaded) ? json.extraction_meta.pages_loaded : null,

    ...buildIssueTerms(json.ipo_structure, json.valuation_information),
    biddingDates: buildBiddingDates(json.ipo_structure),
    financials: buildFinancialTable(json.financial_information),
    kpis: buildKpiTable(json.derived_information),
    parties: buildFilingParties(detail, basic),
    overview: text(json.business_information?.company_overview) || text(json.business_information?.primary_business_summary),
    industry: text(json.business_information?.industry_summary),
    products: (Array.isArray(json.business_information?.product_categories) ? json.business_information.product_categories : [])
      .map(text)
      .filter((value) => value && value.length <= 40),
    objects: (Array.isArray(json.objects_of_issue?.proposed_utilisation) ? json.objects_of_issue.proposed_utilisation : [])
      .map((item) => ({ object: text(item?.object) || text(item?.purpose), amount: text(item?.amount) }))
      .filter((item) => item.object),
    risks: (Array.isArray(json.risk_and_legal_information?.top_risk_factors) ? json.risk_and_legal_information.top_risk_factors : [])
      .map((item) => text(typeof item === 'string' ? item : item?.text))
      .filter(Boolean),
    litigation: buildLitigation(json.risk_and_legal_information),
    litigationNote: prose(json.risk_and_legal_information?.outstanding_litigations_summary),
    news: buildCompanyNews(basic, company),
    faqs: buildFilingFaqs(json),
    narrative: buildNarrative(json.portal_narrative),
  };
};
